import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Receipt, ArrowUpRight, Wallet, ChevronLeft, ChevronRight, Gift } from "lucide-react";
import { formatMoney } from "@/lib/currency";
import api from "@/lib/api";
import BackToDashboard from "@/components/BackToDashboard";
import { useMerchant } from "@/lib/useMerchant";
import DashboardHeader from "@/components/DashboardHeader";
import DashboardFooter from "@/components/DashboardFooter";
import ReferralCard from "@/components/ReferralCard";

const PLAN_LABEL = {
  free: "Free",
  creator: "Creator",
  starter: "Starter",
  growth: "Growth",
  pro: "Pro",
};


function Section({ title, caption, children }) {
  return (
    <div className="rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8">
      <h2 className="font-display text-xl font-black tracking-tight text-ink">{title}</h2>
      {caption && <p className="mt-1 text-sm text-ink/50">{caption}</p>}
      <div className="mt-6">{children}</div>
    </div>
  );
}

const PAYMENT_PAGE_SIZE = 7;

function InvoicePager({ page, totalPages, total, onPage }) {
  if (totalPages <= 1) return null;
  return (
    <div className="mt-5 flex items-center justify-between border-t border-ink/10 pt-4 text-sm text-ink/70">
      <span data-testid="billing-invoices-page-info">
        Page <span className="font-semibold text-ink">{page}</span> of {totalPages} · {total} total
      </span>
      <div className="flex items-center gap-2">
        <button
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          data-testid="billing-invoices-prev"
          className="inline-flex items-center gap-1 rounded-full border border-ink/10 bg-white px-3 py-1.5 disabled:opacity-40"
        >
          <ChevronLeft size={14} /> Prev
        </button>
        <button
          disabled={page >= totalPages}
          onClick={() => onPage(page + 1)}
          data-testid="billing-invoices-next"
          className="inline-flex items-center gap-1 rounded-full border border-ink/10 bg-white px-3 py-1.5 disabled:opacity-40"
        >
          Next <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}

export default function Billing() {
  const navigate = useNavigate();
  const { me } = useMerchant();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState(null);
  const [paymentPage, setPaymentPage] = useState(1);
  const [plansConfig, setPlansConfig] = useState(null);

  useEffect(() => {
    api
      .get("/profile")
      .then(({ data }) => setProfile(data))
      .catch(() => {})
      .finally(() => setLoading(false));
    api
      .get("/billing-history")
      .then(({ data }) => setInvoices(data.items || []))
      .catch(() => setInvoices([]));
    api
      .get("/config")
      .then(({ data }) => setPlansConfig(data.plans || null))
      .catch(() => {});
  }, []);

  const plan = profile?.plan || "free";
  const isPaid = plan !== "free";
  const cycle = profile?.billing_cycle || "monthly";
  const isIndia = (profile?.country || "IN").toUpperCase() === "IN";
  const planCfg = plansConfig?.[plan];
  const price = planCfg
    ? (isIndia
        ? (cycle === "yearly" ? planCfg.price_inr_yearly : planCfg.price_inr)
        : (cycle === "yearly" ? planCfg.price_usd_yearly : planCfg.price_usd))
    : 0;
  // Plan pricing itself is still only quoted in INR/USD — plansConfig has no
  // per-currency price columns yet. Display in the merchant's own currency
  // when we have it, otherwise fall back to the INR/USD split above.
  const planCurrency = profile?.currency || (isIndia ? "INR" : "USD");
  const expiresAt = profile?.subscription_expires_at
    ? new Date(profile.subscription_expires_at)
    : null;
  const isRenewalImminent =
    isPaid && expiresAt && expiresAt.getTime() - Date.now() < 7 * 86400000;

    const paymentTotalPages = invoices ? Math.max(1, Math.ceil(invoices.length / PAYMENT_PAGE_SIZE)) : 1;
    const paginatedInvoices = invoices
      ? invoices.slice((paymentPage - 1) * PAYMENT_PAGE_SIZE, paymentPage * PAYMENT_PAGE_SIZE)
      : [];

  return (
    <div className="min-h-screen bg-cream">
      <DashboardHeader me={me} />
      <main className="mx-auto max-w-3xl px-6 pt-10 md:px-10">
        <BackToDashboard />

        <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Billing</p>
        <h1 className="mt-2 font-display text-4xl font-black leading-tight tracking-tighter md:text-5xl">
          Your plan, in one place.
        </h1>
        <p className="mt-3 max-w-xl text-ink/60">
          Current plan, what it costs, and when it renews.
        </p>

        <div className="mt-8 space-y-6">
          <Section title="Current plan">
            {loading ? (
              <p className="text-sm text-ink/50">Loading…</p>
            ) : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <span className="grid h-11 w-11 flex-none place-items-center rounded-2xl bg-hotpink/10 text-hotpink">
                      <Receipt size={18} />
                    </span>
                    <div>
                      <p className="font-display text-2xl font-black tracking-tight text-ink">
                        {PLAN_LABEL[plan] ?? plan}
                      </p>
                      {isPaid && (
                        <p className="mt-0.5 text-sm text-ink/60">
                          Billed {cycle === "yearly" ? "yearly" : "monthly"}
                        </p>
                      )}
                    </div>
                  </div>

                  {isPaid && (
                    <div className="flex items-center gap-1 text-xl font-bold text-ink">
                      <Wallet size={18} className="text-ink/50" />
                      {formatMoney(price, planCurrency)}
                      <span className="ml-1 text-sm font-normal text-ink/50">
                        / {cycle === "yearly" ? "yr" : "mo"}
                      </span>
                    </div>
                  )}
                </div>

                {isPaid && expiresAt && (
                  <div className="mt-6 rounded-2xl border border-ink/10 bg-cream/60 px-5 py-4 text-sm text-ink/70">
                    {profile?.pending_plan ? (
                      <>
                        You've already paid for{" "}
                        <strong className="text-ink">
                          {PLAN_LABEL[profile.pending_plan] ?? profile.pending_plan}
                          {profile.pending_plan_billing_cycle ? ` (${profile.pending_plan_billing_cycle === "yearly" ? "Yearly" : "Monthly"})` : ""}
                        </strong>
                        {" "}— it'll automatically take effect on{" "}
                        <strong className="text-ink">
                          {expiresAt.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                        </strong>
                        . No further action needed.
                      </>
                    ) : (
                      <>
                        Renews or expires on{" "}
                        <strong className="text-ink">
                          {expiresAt.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                        </strong>
                        {" "}— you'll move to the Free plan unless you renew before then.
                      </>
                    )}
                  </div>
                )}

                {isPaid && expiresAt && expiresAt - Date.now() < 7 * 86400000 && (
                  <div className="mt-6 rounded-2xl border border-hotpink/30 bg-hotpink/5 px-5 py-4 text-sm text-ink/80">
                    {profile?.pending_plan
                      ? `Your ${PLAN_LABEL[profile.pending_plan] ?? profile.pending_plan} plan starts in a few days — nothing else for you to do.`
                      : "Your plan expires soon. Pay again before it expires to keep your current features — otherwise you'll drop to the Free plan automatically."}
                  </div>
                )}

                <button
                  onClick={() => navigate("/plans")}
                  className="cta-glow mt-6 inline-flex items-center gap-2 rounded-full bg-sunset-gradient px-5 py-2.5 text-sm font-semibold text-white"
                  data-testid="billing-change-plan"
                >
                  {isPaid ? (isRenewalImminent ? "Renew plan" : "Change plan") : "Upgrade plan"} <ArrowUpRight size={14} />
                </button>
              </>
            )}
          </Section>

          <Section title="Refer & Earn" caption="Invite other merchants — every paid signup adds free days to your plan.">
            <ReferralCard isPaid={isPaid} ready={!loading} />
          </Section>

          <Section title="Payment History">
            {invoices === null ? (
              <p className="text-sm text-ink/50">Loading…</p>
            ) : invoices.length === 0 ? (
              <p className="text-sm text-ink/50">No payments yet.</p>
            ) : (
              <>
                <div className="space-y-2">
                  {paginatedInvoices.map((inv) =>
                    inv.kind === "referral_credit" ? (
                      <div
                        key={inv.invoice_number}
                        className="flex flex-col gap-2 rounded-2xl border border-hotpink/20 bg-hotpink/5 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between"
                        data-testid="billing-referral-credit-row"
                      >
                        <div className="flex items-center gap-2.5">
                          <Gift size={15} className="flex-none text-hotpink" />
                          <div>
                            <p className="font-semibold text-ink">Referral reward</p>
                            <p className="text-ink/50">
                              {new Date(inv.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                              {" · "}
                              {inv.invoice_number}
                            </p>
                          </div>
                        </div>
                        <p className="font-semibold text-hotpink">+{inv.days_extended} days</p>
                      </div>
                    ) : (
                    <div
                      key={inv.invoice_number}
                      className="flex flex-col gap-2 rounded-2xl border border-ink/10 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div>
                        <p className="font-semibold text-ink">
                          {PLAN_LABEL[inv.plan] ?? inv.plan} · {inv.billing_cycle === "yearly" ? "Yearly" : "Monthly"}
                        </p>
                        <p className="text-ink/50">
                          Paid {new Date(inv.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                          {" · "}
                          {inv.invoice_number}
                        </p>
                        {inv.expiry_at && (
                          <p className="text-ink/50">
                            Valid until {new Date(inv.expiry_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-start gap-1 sm:items-end">
                        {inv.coupon_code && (
                          <span
                            className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-green-700"
                            data-testid="billing-invoice-coupon-badge"
                            title={`${inv.discount_percent}% off applied via coupon ${inv.coupon_code} — this is a promotional discount, not a proration credit.`}
                          >
                            {inv.coupon_code} · {inv.discount_percent}% off
                          </span>
                        )}
                        <p className="font-semibold text-ink">
                          {formatMoney(inv.amount_paise / 100, inv.currency)}
                        </p>
                      </div>
                    </div>
                    )
                  )}
                </div>
                <InvoicePager
                  page={paymentPage}
                  totalPages={paymentTotalPages}
                  total={invoices.length}
                  onPage={setPaymentPage}
                />
              </>
            )}
          </Section>
          <Section title="Billing FAQ">
            <div className="space-y-5">
              <div>
                <p className="font-semibold text-ink">What happens when I upgrade?</p>
                <p className="mt-1 text-sm text-ink/60">
                  It takes effect immediately. You're charged a prorated amount now (full price minus credit for
                  unused days on your current plan), and your renewal date stays the same.
                </p>
              </div>
              <div>
                <p className="font-semibold text-ink">What happens when I downgrade?</p>
                <p className="mt-1 text-sm text-ink/60">
                  You pay the new plan's full price now, but nothing changes right away. You keep your current
                  plan (if on a paid plan) and features until your current billing period ends — then it switches over automatically.
                </p>
              </div>
              <div>
                <p className="font-semibold text-ink">Can I switch from monthly to yearly billing?</p>
                <p className="mt-1 text-sm text-ink/60">
                  Yes. Pay the full annual price now — it's queued, and starts the moment your current monthly
                  period ends, so you don't lose any paid time.
                </p>
              </div>
              <div>
                <p className="font-semibold text-ink">Can I switch from yearly to monthly billing?</p>
                <p className="mt-1 text-sm text-ink/60">
                  Yes. Pay the monthly price now — it's queued, and takes over automatically the moment your
                  current yearly period ends. You won't be dropped to Free in between.
                </p>
              </div>
              <div>
                <p className="font-semibold text-ink">Can I change my tier and billing cycle at the same time?</p>
                <p className="mt-1 text-sm text-ink/60">
                  No — do it in two steps: change your billing cycle first (keeping your current tier), then
                  change tier separately. Both steps can be pre-paid in advance either way.
                </p>
              </div>
              <div>
                <p className="font-semibold text-ink">How can I downgrade to Growth Monthly plan from Pro Yearly plan?</p>
                <p className="mt-1 text-sm text-ink/60">
                  As explained above, do it in two steps.
                  First change your billing cycle by purchasing Pro monthly plan by paying in advance.
                  Then, once Pro Monthly plan is about to expire, purchase Growth Monthly plan by pre-paying.
                  You can always pre-pay for any one plan in advance.
                </p>
              </div>
              <div>
                <p className="font-semibold text-ink">What if I don't renew?</p>
                <p className="mt-1 text-sm text-ink/60">
                  Your plan moves to Free automatically on your expiry date — no charge, no action needed.
                </p>
              </div>
            </div>
          </Section>
        </div>
      </main>
      <DashboardFooter />
    </div>
  );
}
